import axios from "axios";
import { handleResponse, handleError } from "./response";
import * as storageConstants from "../Constants/storageConstant";
import * as commonServices from "./common";
import * as urlconstant from "../Constants/urlConstants";
import { formatPayloadDatesForServer } from "../Utils/dateUtils";

const BASE_URL = urlconstant.BASE_URL;
const cancelTokenMap = {};

// Function to get headers
export const getHeaders = () => {
  return {
    Accept: "application/json",
    Authorization:
      commonServices.getLocalData(storageConstants.AUTH) !== null
        ? `Bearer ${commonServices.getLocalData(storageConstants.AUTH)}`
        : null,
    "Content-Type": "application/json",
  };
};

/**
 * Cancel previous request if it exists and is still pending
 * @param {string} resource
 */
const cancelPreviousRequest = (resource) => {
  if (cancelTokenMap[resource]) {
    cancelTokenMap[resource].cancel("Request cancelled due to new request.");
  }
  cancelTokenMap[resource] = axios.CancelToken.source();
};

/** @param {string} resource */
export const getAll = (resource, model = "") => {
  cancelPreviousRequest(resource);
  const headers = getHeaders();
  const startTime = Date.now(); // Track start time
  const url = `${BASE_URL}${resource}${model}`;

  return axios
    .get(url, { headers, cancelToken: cancelTokenMap[resource].token })
    .then((response) => handleResponse(response, startTime, url))
    .catch((error) => handleError(error, startTime, url));
};

export const get = (resource, id) => {
  const headers = getHeaders();
  const startTime = Date.now(); // Track start time
  const url = `${BASE_URL}${resource}${id}`;

  return axios
    .get(url, { headers })
    .then((response) => handleResponse(response, startTime, url))
    .catch((error) => handleError(error, startTime, url));
};

export const post = (resource, model) => {
  const headers = getHeaders();
  const startTime = Date.now(); // Track start time
  const url = `${BASE_URL}${resource}`;
  const isFormData = model instanceof FormData;

  if (isFormData) {
    delete headers["Content-Type"];
  }

  // FormData is passed through; JSON bodies have Date/moment values rewritten
  // to naive IST strings so they roundtrip correctly with Carbon on the BE.
  const body = isFormData ? model : formatPayloadDatesForServer(model);

  return axios
    .post(url, body, { headers: headers })
    .then((response) => handleResponse(response, startTime, url))
    .catch((error) => handleError(error, startTime, url));
};

export const put = (resource, model) => {
  const headers = getHeaders();
  const startTime = Date.now(); // Track start time
  const url = `${BASE_URL}${resource}`;

  const body = model instanceof FormData ? model : formatPayloadDatesForServer(model);

  return axios
    .put(url, body, { headers: headers })
    .then((response) => handleResponse(response, startTime, url))
    .catch((error) => handleError(error, startTime, url));
};

export const deleteAPI = (id) => {
  const headers = getHeaders();
  const startTime = Date.now(); // Track start time
  const url = `${BASE_URL}${id}`;

  return axios
    .delete(url, { headers: headers })
    .then((response) => handleResponse(response, startTime, url))
    .catch((error) => handleError(error, startTime, url));
};

/**
 * POST that expects a binary (e.g. PDF / CSV) response.
 *
 * Why this exists separately from `post`:
 *  - The default `post` helper pipes everything through `handleResponse`,
 *    which assumes the body is JSON like `{ code, message, data }`. A binary
 *    body would be silently discarded and the caller would never see the
 *    bytes.
 *  - We still defensively check Content-Type so when the BE throws and
 *    returns JSON instead of the file, we surface the message instead of
 *    saving a file containing JSON garbage.
 *
 * @param {string}       resource    URL path (no leading slash).
 * @param {object}       model       Filter payload.
 * @param {object}       opts
 * @param {string}       opts.accept Accept header (e.g. "application/pdf",
 *                                   "text/csv"). Helps the BE pick a format
 *                                   if it ever switches to content negotiation.
 * @param {function}     opts.onProgress  Called with a 0..1 fraction as the
 *                                   download streams. Receives `null` when
 *                                   the server didn't send Content-Length so
 *                                   the caller can switch to "indeterminate".
 * @param {AbortSignal=} opts.signal An AbortController signal so the modal's
 *                                   "Cancel" button can actually cancel the
 *                                   in-flight request.
 *
 * Returns: `{ blob, filename }` on success. The caller is responsible for
 * triggering the browser save.
 */
export const postBlob = (resource, model, opts = {}) => {
  const headers = getHeaders();
  const url = `${BASE_URL}${resource}`;
  const body = model instanceof FormData ? model : formatPayloadDatesForServer(model);
  const accept = opts.accept || "application/pdf";

  return axios
    .post(url, body, {
      headers: { ...headers, Accept: accept },
      responseType: "blob",
      signal: opts.signal,
      onDownloadProgress: (e) => {
        if (typeof opts.onProgress !== "function") return;
        if (e.total) {
          opts.onProgress(Math.min(1, e.loaded / e.total));
        } else if (e.loaded) {
          opts.onProgress(null);
        }
      },
    })
    .then((response) => {
      const contentType = response?.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        return response.data.text().then((text) => {
          let parsed = {};
          try {
            parsed = JSON.parse(text);
          } catch (e) {
            /* keep default */
          }
          throw new Error(parsed?.message || "Failed to download file");
        });
      }

      // Parse Content-Disposition robustly. Symfony / maatwebsite/excel
      // emit `filename=plain.xlsx` for ASCII names but also add
      // `filename*=UTF-8''url-encoded.xlsx` (RFC 5987) when non-ASCII
      // chars are present. Prefer the encoded variant when both exist;
      // fall back to the legacy quoted/unquoted `filename=` form.
      //
      // NOTE: For this header to even reach JS, the BE must list
      // `Content-Disposition` in `config/cors.php` → `exposed_headers`.
      // Without that the browser silently hides it and we end up here
      // with an empty string → the caller's client-side fallback kicks
      // in (which is why the file used to save as "download.xlsx").
      const disposition = response?.headers?.["content-disposition"] || "";
      let filename = "";
      const star = /filename\*\s*=\s*[^']*''([^;\n]+)/i.exec(disposition);
      if (star) {
        try {
          filename = decodeURIComponent(star[1].trim());
        } catch (_e) {
          filename = star[1].trim();
        }
      } else {
        const plain = /filename\s*=\s*(?:"([^"]+)"|([^;\n]+))/i.exec(disposition);
        if (plain) filename = (plain[1] || plain[2] || "").trim();
      }
      if (!filename) filename = "download";

      return { blob: response.data, filename };
    });
};
